import { useRef, useCallback } from 'react';

import { useAppSelector } from 'store';
import { Messages, AppRoutes } from 'config';
import { selectAuthUser } from 'store/slices/auth';
import { useCreateMutation } from 'store/api/stock-transfers';
import { useAlert, useNavigate, handleMutation } from 'hooks';
import { withLtlInvoiceNumber } from 'utils/ltl-invoice-number';
import { StockTransferType } from 'store/api/stock-transfers/types';
import { withEventSplit } from 'components/stock-transfer-event-split/config';
import { useSharedLtlInvoiceConfirm } from 'components/shared-ltl-invoice-confirm';
import { useTransferDefaultEventId } from 'components/stock-transfer-event-split/defaults';

import { Form } from './form';

const Create = () => {
  const authUser = useAppSelector(selectAuthUser);

  const navigate = useNavigate();
  const { showErrorAlert, showSuccessAlert } = useAlert();
  const [create, mutation] = useCreateMutation();
  const defaultEventIdOf = useTransferDefaultEventId();
  const sharedInvoice = useSharedLtlInvoiceConfirm();
  const lastBody = useRef<Parameters<typeof create>[0] | null>(null);

  const onSuccess = useCallback(() => {
    showSuccessAlert(`Transfer ${Messages.ADDED_SUCCESSFULLY}`);
    navigate(AppRoutes.Inventory.Transfers.Home);
  }, []);

  handleMutation({
    ...mutation,
    onSuccess,
    // owner 2026-10-16: an LTL Invoice # already on another record asks
    // first, then saves with the confirmation
    onError: (message, error) => {
      const body = lastBody.current;
      if (
        !body ||
        !sharedInvoice.ask(error, () =>
          create({ ...body, confirmSharedLtlInvoice: true })
        )
      ) {
        showErrorAlert(message);
      }
    },
  });

  return (
    <>
      {sharedInvoice.dialog}
      <Form
        onSubmit={(values) => {
          const body = withEventSplit(
            withLtlInvoiceNumber(values),
            false,
            defaultEventIdOf(values)
          );
          lastBody.current = body;
          return create(body);
        }}
        initialValues={{
          items: [],
          events: [],
          date: undefined,
          ltlPrice: undefined,
          ltlInvoiceNumber: '',
          carrierId: undefined,
          sourceStorageId: undefined,
          companyTransferType: 'storage',
          destinationStorageId: undefined,
          type: StockTransferType.EventToStorage,
          sourceCompanyId: authUser?.company?.id,
        }}
      />
    </>
  );
};

export default Create;
